'use client';

import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { SelectionList } from '@miso/design-system';

// ── 타입 ──────────────────────────────────────────────────────────────────────

/**
 * 완료된 방 편집 시 사용자가 선택할 편집 대상.
 *
 * - 'items': 짐 기록만 편집 (영상 플로우 스킵)
 * - 'video': 영상만 편집 (짐 피커 스킵)
 * - 'both': 짐과 영상 모두 편집 (순서대로 진행)
 */
export type EditTarget = 'items' | 'video' | 'both';

// ── 선택지 정의 ───────────────────────────────────────────────────────────────

const EDIT_OPTIONS = [
  { value: 'items', label: '짐 기록만 편집' },
  { value: 'video', label: '영상만 편집' },
  { value: 'both', label: '짐 기록과 영상 모두 편집' },
];

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export interface EditTargetSelectorProps {
  /** 방 이름 레이블 ("거실", "1번 방" 등) */
  roomLabel: string;
  /** 선택 즉시 호출 */
  onSelect: (target: EditTarget) => void;
  /** 뒤로가기 */
  onBack: () => void;
}

/**
 * 완료된 방의 편집 대상을 선택하는 L1 레이아웃 화면.
 *
 * - 짐 기록만 / 영상만 / 둘 다 3가지 옵션을 제공한다.
 * - 선택 즉시 onSelect 를 호출한다 (autoAdvance 방식).
 */
export function EditTargetSelector({ roomLabel, onSelect, onBack }: EditTargetSelectorProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'white',
        overflow: 'hidden',
      }}
    >
      {/* ── AppBar ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 56,
          px: 1.5,
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onBack} size="small" aria-label="뒤로가기">
          <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── 콘텐츠 ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 4.5, // 36px — L1 레이아웃 가로 패딩과 일치
          pt: 5, // 40px
        }}
      >
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 600,
            color: '#404040',
            letterSpacing: '-0.02em',
            lineHeight: 1.4,
            mb: '58px', // L1 레이아웃 질문↔입력 간격
            whiteSpace: 'pre-line',
          }}
        >
          {`${roomLabel}에서\n무엇을 편집할까요?`}
        </Typography>

        <SelectionList
          mode="single"
          options={EDIT_OPTIONS}
          value={null}
          onChange={(value) => {
            if (value) onSelect(value as EditTarget);
          }}
        />
      </Box>
    </Box>
  );
}
